import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTabsModule } from '@angular/material/tabs';
import { ActivatedRoute, Router } from '@angular/router';
import { DeviceAsset } from '@cdw-ae/common-data';
import { MaterialLibraryModule } from '@cdw-ae/common-frontend-materials';
import { Store } from '@ngrx/store';
import { Subject, takeUntil } from 'rxjs';
import {
  selectCustomerId,
  selectDbUserId,
  selectFbUserId,
  selectUserEmail,
  selectUserName,
} from '../../../../+state/user-identity/user-identity-settings.selectors';
import { GoogleTagManagerService } from '../../../../common/services/analytics/google-tag-manager.service';
import { TailwindColors } from '../../../../common/constants/tailwind-colors';
import {
  DeviceExportImportService,
  type DialogIdentity,
} from '../../../../common/services/device/device-export-import.service';
import { DevicesDataService } from '../../../../common/services/device/devices-data.service';
import { ChromeAuditEventsComponent } from './tabs/chrome-audit-events/chrome-audit-events.component';
import { DeviceInformationComponent } from './tabs/device-information/device-information.component';
import { DeviceUsageComponent } from './tabs/device-usage/device-usage.component';
import { GopherBuddySessionsComponent } from './tabs/gopher-buddy-sessions/gopher-buddy-sessions.component';

@Component({
  selector: 'device-summary',
  standalone: true,
  imports: [
    CommonModule,
    MatCardModule,
    MaterialLibraryModule,
    MatTabsModule,
    MatIconModule,
    MatSelectModule,
    MatFormFieldModule,
    MatSnackBarModule,
    DeviceInformationComponent,
    DeviceUsageComponent,
    GopherBuddySessionsComponent,
    ChromeAuditEventsComponent,
    MatProgressSpinnerModule,
  ],
  templateUrl: './device-summary.component.html',
  styleUrl: './device-summary.component.scss',
})
export class DeviceSummaryComponent implements OnInit, OnDestroy {
  public selectedIndex = 0;
  public deviceId: string | null = null;
  public deviceData: DeviceAsset | null = null;
  public loading = false;
  public error: string | null = null;
  private destroy$ = new Subject<void>();

  // EDUS-2699: user identity for the "Export Audit History" button.
  private customerId = '';
  private fbUserId = '';
  private dbUserId = 0;
  private userEmail = '';
  private userName?: string;

  public activeTabTextColor: string = TailwindColors.tertiaryBlueLink;
  public inactiveTabTextColor: string = TailwindColors.gray200;
  public customTabFontWeight = '700';

  constructor(
    private devicesDataService: DevicesDataService,
    private route: ActivatedRoute,
    private router: Router,
    private store: Store,
    private deviceExportImport: DeviceExportImportService,
    private gtmService: GoogleTagManagerService
  ) {
    // Get device ID from route params
    this.deviceId = this.route.snapshot.paramMap.get('id');
  }

  ngOnInit(): void {
    this.subscribeToIdentity();

    if (this.deviceId) {
      this.loadDeviceData();
    } else {
      this.error = 'No device ID provided.';
    }
  }

  public loadDeviceData(): void {
    if (!this.deviceId) return;

    this.loading = true;
    this.error = null;
    this.devicesDataService
      .getDeviceById(this.deviceId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (device) => {
          this.deviceData = device;
          this.loading = false;
          if (!device) {
            this.error = 'Device not found or could not be loaded.';
          }
        },
        error: (err) => {
          this.error = 'Failed to load device data. Please try again.';
          this.loading = false;
        },
      });
  }

  public onTabChange(tabIndex: number) {
    this.selectedIndex = tabIndex;

    // Track tab switches for product analytics (URL doesn't change, so GA4 won't catch it)
    const tabNames = [
      'Device Information',
      'Device Usage',
      'Gopher Buddy Sessions',
      'Chrome Audit Events',
    ];
    this.gtmService.pushUserAction({
      category: 'device_summary_tabs',
      action: 'tab_viewed',
      metadata: {
        tab_name: tabNames[tabIndex],
        tab_index: tabIndex,
      },
    });
  }

  public goBackToDeviceSearch(): void {
    this.router.navigate(['/deviceSearch']);
  }

  private subscribeToIdentity(): void {
    this.store
      .select(selectCustomerId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((id) => {
        this.customerId = id;
      });
    this.store
      .select(selectFbUserId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((uid) => {
        this.fbUserId = uid;
      });
    this.store
      .select(selectDbUserId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((id) => {
        this.dbUserId = id;
      });
    this.store
      .select(selectUserEmail)
      .pipe(takeUntil(this.destroy$))
      .subscribe((email) => {
        this.userEmail = email;
      });
    this.store
      .select(selectUserName)
      .pipe(takeUntil(this.destroy$))
      .subscribe((name) => {
        this.userName = name || undefined;
      });
  }

  /**
   * EDUS-2699: export this device's complete audit-change history. The row set
   * is resolved server-side from the device's `assetId` (the internal uuid in
   * the route); we only pass identity + the device id and let the dialog collect
   * file/format/recipient choices.
   */
  public exportAuditHistory(): void {
    if (!this.deviceId) {
      return;
    }

    const identity: DialogIdentity = {
      fbUserId: this.fbUserId,
      dbUserId: this.dbUserId,
      userEmail: this.userEmail,
      userName: this.userName,
      customerId: parseInt(this.customerId, 10),
    };

    this.deviceExportImport.openDeviceAuditHistoryExportDialog({
      deviceId: this.deviceId,
      identity,
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
